import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import {
  getReminders,
  createReminder,
  updateReminder,
  deleteReminder,
  getUpcomingReminders,
  syncBirthdayReminders,
  getHolidayTemplates,
  createReminderFromHoliday
} from '@/api/reminder';
import { CreateReminderRequest, UpdateReminderRequest } from '@/types/reminder';

export const useReminders = (filters?: { is_active?: boolean; reminder_type?: string; days?: number }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Get all reminders
  const {
    data: reminders = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ['reminders', user?.id, filters],
    queryFn: () => user ? getReminders(user.id, filters) : Promise.resolve([]),
    enabled: !!user,
  });

  // Get upcoming reminders
  const {
    data: upcomingReminders = [],
    isLoading: isLoadingUpcoming
  } = useQuery({
    queryKey: ['upcoming-reminders', user?.id, filters?.days],
    queryFn: () => user ? getUpcomingReminders(user.id, filters?.days || 7) : Promise.resolve([]),
    enabled: !!user,
  });

  // Create reminder mutation
  const createReminderMutation = useMutation({
    mutationFn: (reminder: CreateReminderRequest) => {
      if (!user) throw new Error('User not authenticated');
      return createReminder(user.id, reminder);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] });
      queryClient.invalidateQueries({ queryKey: ['upcoming-reminders'] });
      toast({
        title: 'Reminder created successfully'
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to create reminder',
        description: error.message,
        variant: 'destructive'
      });
    },
  });

  // Update reminder mutation
  const updateReminderMutation = useMutation({
    mutationFn: ({ reminderId, updates }: { reminderId: number; updates: UpdateReminderRequest }) => {
      if (!user) throw new Error('User not authenticated');
      return updateReminder(user.id, reminderId, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] });
      queryClient.invalidateQueries({ queryKey: ['upcoming-reminders'] });
      toast({
        title: 'Reminder updated successfully'
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update reminder',
        description: error.message,
        variant: 'destructive'
      });
    },
  });

  // Delete reminder mutation
  const deleteReminderMutation = useMutation({
    mutationFn: (reminderId: number) => {
      if (!user) throw new Error('User not authenticated');
      return deleteReminder(user.id, reminderId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] });
      queryClient.invalidateQueries({ queryKey: ['upcoming-reminders'] });
      toast({
        title: 'Reminder deleted successfully'
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to delete reminder',
        description: error.message,
        variant: 'destructive'
      });
    },
  });

  // Sync birthdays mutation
  const syncBirthdaysMutation = useMutation({
    mutationFn: () => {
      if (!user) throw new Error('User not authenticated');
      return syncBirthdayReminders(user.id);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] });
      queryClient.invalidateQueries({ queryKey: ['upcoming-reminders'] });
      toast({
        title: 'Birthday sync completed',
        description: `Created ${result.created} new reminders, updated ${result.updated} existing ones`
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to sync birthdays',
        description: error.message,
        variant: 'destructive'
      });
    },
  });

  return {
    reminders,
    upcomingReminders,
    isLoading,
    isLoadingUpcoming,
    error,
    createReminder: createReminderMutation.mutate,
    updateReminder: updateReminderMutation.mutate,
    deleteReminder: deleteReminderMutation.mutate,
    syncBirthdays: syncBirthdaysMutation.mutate,
    syncBirthdaysData: syncBirthdaysMutation.data,
    isCreating: createReminderMutation.isPending,
    isUpdating: updateReminderMutation.isPending,
    isDeleting: deleteReminderMutation.isPending,
    isSyncing: syncBirthdaysMutation.isPending,
  };
};

export const useHolidayTemplates = (countryCode: string = 'DE') => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['holiday-templates', user?.id, countryCode],
    queryFn: () => user ? getHolidayTemplates(user.id, countryCode) : Promise.resolve([]),
    enabled: !!user,
  });
};

export const useCreateReminderFromHoliday = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ holidayId, advanceNotice }: { holidayId: number; advanceNotice: number }) => {
      if (!user) throw new Error('User not authenticated');
      return createReminderFromHoliday(user.id, holidayId, advanceNotice);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] });
      queryClient.invalidateQueries({ queryKey: ['upcoming-reminders'] });
      toast({
        title: 'Holiday reminder created'
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to create holiday reminder',
        description: error.message,
        variant: 'destructive'
      });
    },
  });
};